/**
 * Test Utility for FCM + Service Worker + Toast Flow
 */

export const testNavigateToChat = (threadId = 'test-thread-123'): void => {
  console.log('🧪 Testing NAVIGATE_TO_CHAT message with threadId:', threadId);
  
  window.postMessage({
    type: 'NAVIGATE_TO_CHAT',
    thread_id: threadId
  }, window.location.origin);
  
  console.log('✅ Message posted! Check if Main.tsx navigated to the chat.');
};

export const testSessionUpdate = (payload: Record<string, unknown> = {}): void => {
  console.log('🧪 Testing SESSION_UPDATE message');
  
  window.postMessage({
    type: 'SESSION_UPDATE',
    payload: {
      notification: {
        title: 'Test Notification',
        body: 'This is a test notification'
      },
      data: {
        thread_id: 'test-thread-456',
        ttl: 60,
        ...(payload.data as object)
      },
      ...payload
    }
  }, window.location.origin);
  
  console.log('✅ Message posted! Check if sessions list refreshed.');
};

export const testForegroundFCM = (): void => {
  console.log('🧪 Simulating Foreground FCM Message');
  
  setTimeout(() => {
    console.log('   👉 Simulating toast click...');
    testNavigateToChat('foreground-test-789');
  }, 1000);
  
  setTimeout(() => {
    console.log('   👉 Triggering session update...');
    testSessionUpdate({
      data: {
        thread_id: 'foreground-test-789',
        ttl: 3600
      }
    });
  }, 1500);
};

export const testBackgroundFCM = (): void => {
  console.log('🧪 Simulating Background FCM Message');
  
  setTimeout(() => {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      window.postMessage({
        type: 'NAVIGATE_TO_CHAT',
        thread_id: 'background-test-999',
        source: 'service-worker'
      }, window.location.origin);
    } else {
      console.warn('   ⚠️  No active service worker found');
      testNavigateToChat('background-test-999');
    }
  }, 1000);
};

export const testRapidMessages = (count = 5): void => {
  console.log(`🧪 Testing ${count} rapid messages`);
  
  for (let i = 0; i < count; i++) {
    setTimeout(() => {
      testSessionUpdate({
        data: {
          thread_id: `rapid-test-${i}`,
          ttl: 30
        }
      });
      console.log(`   📨 Message ${i + 1}/${count} sent`);
    }, i * 100);
  }
};

export const testInvalidMessage = (): void => {
  console.log('🧪 Testing invalid message (should be ignored)');
  
  window.postMessage({
    type: 'INVALID_TYPE',
    malicious: 'data'
  }, window.location.origin);
  
  console.log('✅ Invalid message posted. It should be ignored by listeners.');
};

export const testWrongOrigin = (): void => {
  console.log('🧪 Testing message from wrong origin (security test)');
  
  window.postMessage({
    type: 'NAVIGATE_TO_CHAT',
    thread_id: 'malicious-thread'
  }, 'https://evil.com');
  
  console.log('   Message should be rejected by origin check');
};

export const monitorMessages = (duration = 30000): (() => void) => {
  console.log(`🔍 Monitoring window messages for ${duration/1000} seconds...`);
  
  const handler = (event: MessageEvent): void => {
    console.log('📨 Message received:', {
      type: event.data?.type,
      origin: event.origin,
      data: event.data,
      timestamp: new Date().toISOString()
    });
  };
  
  window.addEventListener('message', handler);
  
  setTimeout(() => {
    window.removeEventListener('message', handler);
    console.log('✅ Monitoring stopped');
  }, duration);
  
  return () => window.removeEventListener('message', handler);
};

export const checkListeners = (): void => {
  console.log('🔍 Checking event listeners...');
  console.log('   - Service Worker registered:', !!navigator.serviceWorker.controller);
  console.log('   - Current URL:', window.location.href);
  console.log('   - Origin:', window.location.origin);
};

const FCMTestUtils = {
  testNavigateToChat,
  testSessionUpdate,
  testForegroundFCM,
  testBackgroundFCM,
  testRapidMessages,
  testInvalidMessage,
  testWrongOrigin,
  monitorMessages,
  checkListeners
};

if (typeof window !== 'undefined') {
  (window as Window & { FCMTestUtils?: typeof FCMTestUtils }).FCMTestUtils = FCMTestUtils;
  console.log('✅ FCM Test Utils loaded! Available at window.FCMTestUtils');
}

export default FCMTestUtils;
